import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { useCart } from '../../../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';
import ActionsMenu from '../../atoms/ActionsMenu/ActionsMenu';
import MobileMenu from '../../atoms/MobileMenu/MobileMenu';
import LanguageSwitcher from '../../atoms/LanguageSwitcher';
import ThemeSwitcher from '../../atoms/ThemeSwitcher/ThemeSwitcher';
import NotificationBell from '../../molecules/NotificationBell/NotificationBell';
import Icon from '../../atoms/Icon/Icon';
import Card from '../../atoms/Card';
import Button from '../../atoms/Button';
import Login from '@/pages/public/Login';
import Register from '@/pages/public/Register';
import './PublicLayout.scss';

export interface PublicLayoutProps {
  children: React.ReactNode;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { cart } = useCart();
  const { isAuthenticated } = useAuth();
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register' | null>(null);
  
  // Show notifications for authenticated users only
  // Anonymous users see notifications in OrderConfirmation page (inside OrderNotificationProvider)
  const showNotifications = isAuthenticated;

  const logoSrc = theme === 'dark' 
    ? '/images/logo/prior-logo-black.png'
    : '/images/logo/prior-logo-white.png';

  const logoSrcFooter = theme === 'dark' 
    ? '/images/logo/prior-logo-black.png'
    : '/images/logo/prior-logo-white.png';
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  

  // Calculate total items in cart
  const cartItemCount = cart?.items?.reduce((total, item) => total + item.quantity, 0) || 0;

  useEffect(() => {
    document.body.style.overflow = authModalMode ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [authModalMode]);

  useEffect(() => {
    if (isAuthenticated && authModalMode) {
      setAuthModalMode(null);
    }
  }, [isAuthenticated, authModalMode]);

  return (
    <div className="public-layout">
      {/* Header */}
      <header className="public-layout__header">
        <div className="container">
          {/* Desktop Header */}
          <div className="public-layout__header-content public-layout__header-content--desktop">
            <div className="public-layout__logo">
              <button
                className="public-layout__logo public-layout__logo--clickable"
                onClick={() => navigate('/events')}
                aria-label={t('navigation.welcome')}
              >
                <img
                  src={logoSrc}
                  alt="Prior"
                  className="public-layout__logo-img"
                />
              </button>
            </div>
              
            <div className="public-layout__actions">
              
              <LanguageSwitcher />
              <ThemeSwitcher />
              {showNotifications && <NotificationBell />}
              <ActionsMenu
                onOpenLogin={() => setAuthModalMode('login')}
                onOpenRegister={() => setAuthModalMode('register')}
              />
              
            </div>
          </div>

          {/* Mobile Header */}
          <div className="public-layout__header-content public-layout__header-content--mobile">
            {/* Columna 1: Logo */}
            <div className="public-layout__mobile-logo">
              <button
                className="public-layout__mobile-logo public-layout__mobile-logo--clickable"
                onClick={() => navigate('/events')}
                aria-label={t('navigation.welcome')}
              >
                <img
                  src={logoSrc}
                  alt="Prior"
                  className="public-layout__logo-img"
                />
              </button>
            </div>

            <div className="public-layout__mobile-header-trailing">
              

              {showNotifications && (
                <NotificationBell />
              )}

              {cartItemCount > 0 && (
                <button
                  className="public-layout__mobile-cart"
                  onClick={() => navigate('/cart')}
                  title={t('cart.title')}
                >
                  <Icon name="carrito" size={20} alt={t('cart.title')} />
                  <span className="public-layout__cart-badge">{cartItemCount}</span>
                </button>
              )}

              <MobileMenu
                logoSrc={logoSrcFooter}
                isActive={isActive}
                variant="public"
                onOpenLogin={() => setAuthModalMode('login')}
                onOpenRegister={() => setAuthModalMode('register')}
              />
            </div>
          </div>
        </div>
      </header>

      {authModalMode && (
        <div
          className="public-layout__auth-modal"
          onClick={() => setAuthModalMode(null)}
        >
          <Card
            variant="elevated"
            padding="md"
            className="public-layout__auth-modal-card"
          >
            <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
              <div className="public-layout__auth-modal-header">
                <div className="public-layout__auth-modal-tabs">
                  <Button
                    variant={authModalMode === 'login' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setAuthModalMode('login')}
                  >
                    {t('navigation.login')}
                  </Button>
                  <Button
                    variant={authModalMode === 'register' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setAuthModalMode('register')}
                  >
                    {t('navigation.register')}
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setAuthModalMode(null)}
                  aria-label={t('common.close')}
                >
                  ×
                </Button>
              </div>
              <div className="public-layout__auth-modal-content">
                {authModalMode === 'login' ? <Login /> : <Register />}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <main className="public-layout__main">
        <div className="container">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="public-layout__footer">
        <div className="container">
          <div className="public-layout__footer-content">
            <div className="public-layout__footer-section">
              <div className="public-layout__footer-logo">
                <img
                  src={logoSrcFooter}
                  alt="Prior"
                  className="public-layout__footer-logo-img"
                />
              </div>
              <p>{t('footer.description')}</p>
            </div>
            <div className='public-layout__footer-group'>
              <div className="public-layout__footer-section">
                <h4>{t('footer.quickLinks')}</h4>
                <ul>
                  <li><button onClick={() => navigate('/events')}>{t('navigation.events')}</button></li>
                  <li><button onClick={() => navigate('/login')}>{t('navigation.login')}</button></li>
                </ul>
              </div>
              <div className="public-layout__footer-section">
                <h4>{t('footer.contact')}</h4>
                <p>{t('footer.email')}</p>
                <p>{t('footer.phone')}</p>
              </div>
            </div>
          </div>
          <div className="public-layout__footer-bottom">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout; 